import React, { useState, useEffect, useMemo } from 'react'
import config from '../../config.js'
import { formatCurrency } from './formatters'
import { apiErrorMessage } from '../../utils/apiError';

const NESTER = config.NESTER_API_URL

/**
 * Remnants tab — the remnant library: list, filter, add, edit, consume, retire.
 *
 * For the current project, we auto-query /remnants/match to surface candidate
 * remnants matching the project's material spec and highlight them at the
 * top. Operator clicks "Use this remnant" to mark it reserved against the
 * project.
 */
export default function NesterRemnantsPanel({
  colors,
  project,
  materialGroups = [],
  parts = [],
  nestResult,
}) {
  const [remnants, setRemnants] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('available')
  const [query, setQuery] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [matches, setMatches] = useState([])

  useEffect(() => { loadRemnants() }, [statusFilter]) // eslint-disable-line
  useEffect(() => { loadMatches() }, [project?.project_id, materialGroups.length]) // eslint-disable-line

  async function loadRemnants() {
    setLoading(true); setError(null)
    try {
      const url = statusFilter === 'all'
        ? `${NESTER}/remnants`
        : `${NESTER}/remnants?status=${statusFilter}`
      const res = await fetch(url)
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setRemnants(await res.json())
    } catch (e) {
      setError(e.message || 'Failed to load remnants')
    }
    setLoading(false)
  }

  async function loadMatches() {
    if (!project) return
    // Collect material+thickness combos to probe
    const specs = []
    if (materialGroups.length > 0) {
      materialGroups.forEach(g => {
        if (g.material_type && g.thickness_display) {
          specs.push({
            material_type: g.material_type,
            thickness_display: g.thickness_display,
            group_id: g.group_id,
          })
        }
      })
    } else if (project.material_type && project.thickness_display) {
      specs.push({
        material_type: project.material_type,
        thickness_display: project.thickness_display,
        group_id: null,
      })
    }
    if (specs.length === 0) return

    const results = []
    for (const s of specs) {
      try {
        const params = new URLSearchParams({
          material_type: s.material_type,
          thickness_display: s.thickness_display,
        })
        const res = await fetch(`${NESTER}/remnants/match?${params}`)
        if (!res.ok) continue
        const data = await res.json()
        results.push(...data.map(m => ({ ...m, group_id: s.group_id })))
      } catch (e) { /* continue */ }
    }
    setMatches(results)
  }

  async function addRemnant(body) {
    try {
      const res = await fetch(`${NESTER}/remnants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await loadRemnants()
      setShowAdd(false)
    } catch (e) {
      alert('Failed to add remnant: ' + e.message)
    }
  }

  async function retireRemnant(id) {
    if (!window.confirm('Retire this remnant? It will no longer appear as a candidate for future nests.')) return
    try {
      await fetch(`${NESTER}/remnants/${id}`, { method: 'DELETE' })
      await loadRemnants()
    } catch (e) {
      alert('Failed: ' + e.message)
    }
  }

  async function consumeRemnant(id) {
    try {
      const res = await fetch(`${NESTER}/remnants/${id}/consume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consumed_project_id: project?.project_id || null,
          consumed_nest_id: nestResult?.nest_id || null,
        }),
      })
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await loadRemnants()
      loadMatches()
    } catch (e) {
      alert('Failed: ' + e.message)
    }
  }

  const filtered = useMemo(() => {
    if (!query.trim()) return remnants
    const q = query.trim().toLowerCase()
    return remnants.filter(r =>
      r.sku.toLowerCase().includes(q)
      || (r.material_type || '').toLowerCase().includes(q)
      || (r.thickness_display || '').toLowerCase().includes(q)
      || (r.location || '').toLowerCase().includes(q)
    )
  }, [remnants, query])

  const totalValue = remnants
    .filter(r => r.status === 'available')
    .reduce((s, r) => s + (r.cost_basis_dollars || 0), 0)

  return (
    <div style={{ padding: 16 }}>
      {/* Matches banner */}
      {matches.filter(m => m.fits).length > 0 && (
        <div style={{
          padding: '10px 14px', marginBottom: 14,
          background: colors.successSoft,
          border: `1px solid ${colors.success}`,
          borderRadius: 6,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: colors.success, marginBottom: 6 }}>
            ♻ {matches.filter(m => m.fits).length} remnant{matches.filter(m => m.fits).length === 1 ? '' : 's'} match this project's material
          </div>
          <div style={{ fontSize: 12, color: colors.text }}>
            Consider using these before buying new sheets. Click Consume below to allocate one.
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        marginBottom: 14,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>♻ Remnant Library</div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
            Track leftover sheet offcuts so future nests can reuse them before ordering fresh material.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            Inventory Value
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: colors.accent }}>
            {formatCurrency(totalValue)}
          </div>
        </div>
        <button onClick={() => setShowAdd(true)}
          style={{
            padding: '6px 14px', fontSize: 13, fontWeight: 700,
            border: 'none', borderRadius: 4,
            background: colors.accent, color: '#fff', cursor: 'pointer',
          }}>
          + Record remnant
        </button>
      </div>

      {error && (
        <div style={{
          padding: 10, marginBottom: 10,
          background: colors.dangerSoft, border: `1px solid ${colors.danger}`,
          borderRadius: 4, color: colors.danger, fontSize: 12,
        }}>{error}</div>
      )}

      {/* Filter row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <input value={query} onChange={e => setQuery(e.target.value)}
          placeholder="🔍 Search by SKU, material, location…"
          style={{
            padding: '5px 10px', fontSize: 12, width: 300,
            border: `1px solid ${colors.border}`, borderRadius: 4,
            background: colors.panel, color: colors.text,
          }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{
            padding: '5px 10px', fontSize: 12,
            border: `1px solid ${colors.border}`, borderRadius: 4,
            background: colors.panel, color: colors.text,
          }}>
          <option value="available">Available</option>
          <option value="reserved">Reserved</option>
          <option value="consumed">Consumed</option>
          <option value="retired">Retired</option>
          <option value="all">All</option>
        </select>
        <div style={{ fontSize: 12, color: colors.textMuted }}>
          {filtered.length} of {remnants.length}
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <RemnantAddForm
          colors={colors}
          project={project}
          nestResult={nestResult}
          onCancel={() => setShowAdd(false)}
          onSubmit={addRemnant}
        />
      )}

      {/* Table */}
      <div style={{
        border: `1px solid ${colors.border}`, borderRadius: 6,
        overflow: 'auto',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: colors.panelAlt }}>
            <tr>
              {['SKU', 'Material', 'Thickness', 'Size (in)', 'Weight', 'Location', 'Source', 'Cost', 'Status', ''].map(h => (
                <th key={h} style={{
                  padding: '6px 10px', fontSize: 11, fontWeight: 700,
                  color: colors.textMuted, textAlign: 'left',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  borderBottom: `1px solid ${colors.border}`,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="10" style={{ padding: 20, textAlign: 'center', color: colors.textMuted }}>Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="10" style={{ padding: 30, textAlign: 'center', color: colors.textMuted }}>
                {remnants.length === 0
                  ? 'No remnants yet. Click "Record remnant" to add one from a completed cut job.'
                  : 'No remnants match your filter.'}
              </td></tr>
            )}
            {!loading && filtered.map(r => (
              <tr key={r.remnant_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={td(colors)}>
                  <strong style={{ fontFamily: 'ui-monospace, monospace' }}>{r.sku}</strong>
                </td>
                <td style={td(colors)}>{r.material_type}{r.surface_finish && ` · ${r.surface_finish}`}</td>
                <td style={td(colors)}>{r.thickness_display}</td>
                <td style={{ ...td(colors), fontVariantNumeric: 'tabular-nums' }}>
                  {Number(r.width_in).toFixed(1)} × {Number(r.length_in).toFixed(1)}
                </td>
                <td style={{ ...td(colors), fontVariantNumeric: 'tabular-nums' }}>
                  {r.weight_lbs ? `${Number(r.weight_lbs).toFixed(1)} lb` : '—'}
                </td>
                <td style={td(colors)}>{r.location || '—'}</td>
                <td style={td(colors)}>
                  {r.source_project_id ? <span>Proj #{r.source_project_id}</span> : '—'}
                </td>
                <td style={td(colors)}>{formatCurrency(r.cost_basis_dollars)}</td>
                <td style={td(colors)}>
                  <StatusPill colors={colors} status={r.status} />
                </td>
                <td style={{ ...td(colors), textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {r.status === 'available' && project && (
                    <button onClick={() => consumeRemnant(r.remnant_id)}
                      style={actionBtn(colors, colors.success)}>
                      Consume
                    </button>
                  )}
                  {r.status !== 'consumed' && r.status !== 'retired' && (
                    <button onClick={() => retireRemnant(r.remnant_id)}
                      style={{ ...actionBtn(colors, colors.danger), marginLeft: 4 }}>
                      Retire
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function RemnantAddForm({ colors, project, nestResult, onCancel, onSubmit }) {
  const [form, setForm] = useState(() => ({
    material_type: project?.material_type || '',
    thickness_display: project?.thickness_display || '',
    thickness_in: project?.thickness_in || '',
    surface_finish: project?.surface_finish || '',
    width_in: '',
    length_in: '',
    weight_lbs: '',
    location: '',
    notes: '',
    cost_basis_dollars: '',
    source_project_id: project?.project_id || '',
    source_nest_id: nestResult?.nest_id || '',
  }))

  function set(k, v) { setForm(prev => ({ ...prev, [k]: v })) }

  function submit() {
    const body = {
      material_type: form.material_type,
      thickness_display: form.thickness_display,
      thickness_in: form.thickness_in ? parseFloat(form.thickness_in) : null,
      surface_finish: form.surface_finish || null,
      width_in: parseFloat(form.width_in),
      length_in: parseFloat(form.length_in),
      weight_lbs: form.weight_lbs ? parseFloat(form.weight_lbs) : null,
      location: form.location || null,
      notes: form.notes || null,
      cost_basis_dollars: form.cost_basis_dollars ? parseFloat(form.cost_basis_dollars) : null,
      source_project_id: form.source_project_id ? parseInt(form.source_project_id) : null,
      source_nest_id: form.source_nest_id ? parseInt(form.source_nest_id) : null,
    }
    if (!body.material_type || !body.thickness_display || !body.width_in || !body.length_in) {
      alert('Material, thickness, width, and length are required.')
      return
    }
    onSubmit(body)
  }

  const inputStyle = {
    padding: '4px 8px', fontSize: 13,
    border: `1px solid ${colors.border}`, borderRadius: 4,
    background: colors.panel, color: colors.text,
    boxSizing: 'border-box', width: '100%',
  }

  return (
    <div style={{
      padding: 14, marginBottom: 14,
      background: colors.panelAlt,
      border: `1px solid ${colors.border}`, borderRadius: 6,
    }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: colors.text, marginBottom: 10 }}>
        Record a new remnant
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
        <Field label="Material *" colors={colors}>
          <input value={form.material_type} onChange={e => set('material_type', e.target.value)}
            style={inputStyle} placeholder="e.g., Mild Steel A36" />
        </Field>
        <Field label="Thickness *" colors={colors}>
          <input value={form.thickness_display} onChange={e => set('thickness_display', e.target.value)}
            style={inputStyle} placeholder="e.g., 14ga" />
        </Field>
        <Field label="Finish" colors={colors}>
          <input value={form.surface_finish} onChange={e => set('surface_finish', e.target.value)}
            style={inputStyle} />
        </Field>
        <Field label="Width (in) *" colors={colors}>
          <input type="number" step="0.125" value={form.width_in} onChange={e => set('width_in', e.target.value)}
            style={inputStyle} />
        </Field>
        <Field label="Length (in) *" colors={colors}>
          <input type="number" step="0.125" value={form.length_in} onChange={e => set('length_in', e.target.value)}
            style={inputStyle} />
        </Field>
        <Field label="Weight (lb)" colors={colors}>
          <input type="number" step="0.1" value={form.weight_lbs} onChange={e => set('weight_lbs', e.target.value)}
            style={inputStyle} />
        </Field>
        <Field label="Cost basis ($)" colors={colors}>
          <input type="number" step="0.01" value={form.cost_basis_dollars}
            onChange={e => set('cost_basis_dollars', e.target.value)}
            style={inputStyle} placeholder="What it cost to buy" />
        </Field>
        <Field label="Location" colors={colors}>
          <input value={form.location} onChange={e => set('location', e.target.value)}
            style={inputStyle} placeholder="e.g., Rack B-4" />
        </Field>
      </div>
      <Field label="Notes" colors={colors} style={{ marginTop: 10 }}>
        <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
      </Field>
      <div style={{ marginTop: 10, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={onCancel}
          style={{
            padding: '5px 14px', border: `1px solid ${colors.border}`,
            background: 'transparent', color: colors.text,
            borderRadius: 4, cursor: 'pointer', fontSize: 12,
          }}>Cancel</button>
        <button onClick={submit}
          style={{
            padding: '5px 14px', border: 'none',
            background: colors.accent, color: '#fff',
            borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 700,
          }}>Add remnant</button>
      </div>
    </div>
  )
}

function Field({ label, colors, children, style }) {
  return (
    <div style={style}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: colors.textMuted,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        marginBottom: 3,
      }}>{label}</div>
      {children}
    </div>
  )
}

function StatusPill({ colors, status }) {
  const map = {
    available: { bg: colors.successSoft, fg: colors.success, label: 'Available' },
    reserved: { bg: colors.warnSoft, fg: colors.warn, label: 'Reserved' },
    consumed: { bg: colors.panelAlt, fg: colors.textMuted, label: 'Consumed' },
    retired: { bg: colors.dangerSoft, fg: colors.danger, label: 'Retired' },
  }
  const s = map[status] || map.available
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 10,
      fontSize: 10, fontWeight: 700,
      background: s.bg, color: s.fg,
    }}>{s.label}</span>
  )
}

const td = (c) => ({
  padding: '5px 10px',
  color: c.text,
  fontSize: 12,
})

const actionBtn = (c, accent) => ({
  padding: '3px 10px', fontSize: 11, fontWeight: 600,
  border: `1px solid ${accent}`, color: accent,
  background: 'transparent', borderRadius: 3,
  cursor: 'pointer',
})
